import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import GSApi from 'api/Api';
import { parseBookmarkItems } from 'utils/fnParser';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { BookmarkCategory, BookmarkItem, BookmarkSectionType, BookmarksResponse } from 'types/Bookmarks';
import { useAuth } from 'providers/useAuth/AuthContext';
import { useGlobalNetworkError, useGlobalNoInternet } from 'hooks/withNetworkCheck/withNetworkCheck';

const useBookmarksService = () => {
    const { onNoInternet, noInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();
    const queryClient = useQueryClient();

    const { response: bookmarksResponse, fetcher: bookmarksFetcher } = useDataFetcher<BookmarksResponse, null>(
        () => GSApi.getMyFavorites(),
        onNoInternet,
        onNetworkError
    );

    const bookmarkActions = {
        [BookmarkSectionType.MOVIES]: {
            add: GSApi.createVodFavorite,
            remove: GSApi.removeVodFavorite,
        },
        [BookmarkSectionType.VOD_SERIES]: {
            add: GSApi.createVodSeriesFavorite,
            remove: GSApi.removeVodSeriesFavorite,
        },
        [BookmarkSectionType.BROADCASTS_AND_SERIES]: {
            add: GSApi.createProgramSeriesFavorite,
            remove: GSApi.removeProgramSeriesFavorite,
        },
        [BookmarkSectionType.PROGRAMS]: {
            add: GSApi.createListingProgramFavorite,
            remove: GSApi.removeListingProgramFavorite,
        },
        [BookmarkSectionType.LISTINGS]: {
            add: GSApi.createListingFavorite,
            remove: GSApi.removeListingFavorite,
        },
    };

    const toggleBookmark = async (id: string, type: BookmarkSectionType, action: 'add' | 'remove') => {
        const actionMap = bookmarkActions[type];
        if (actionMap) {
            await actionMap[action](id);
        }
    };

    const [bookmarkItems, setBookmarkItems] = useState<BookmarkItem[]>(null);
    const topCategoriesRef = useRef(null);
    const childCategoriesRef = useRef<{ [key: string]: BookmarkCategory }>(null);

    const { config } = useConfig();
    const { isGuest, isLoggedIn } = useAuth();

    useEffect(() => {
        if (config && isLoggedIn) {
            if (!noInternet) {
                if (!isGuest) {
                    bookmarksFetcher(null);
                }

                if (isGuest) {
                    setBookmarkItems([]);
                }
            }
        }
    }, [config, isLoggedIn, isGuest, noInternet]);

    useEffect(() => {
        if (bookmarksResponse) {
            setBookmarkItems(parseBookmarkItems(bookmarksResponse));
        }
    }, [bookmarksResponse]);

    const isBookmarked = (id: string, type: BookmarkSectionType): boolean => {
        if (isGuest) return false;
        const bookmarkItem = bookmarkItems?.find((item) => item?.name === type);
        return bookmarkItem ? Boolean(bookmarkItem.bookmarks[id]) : false;
    };

    const toggleBookmarkItem = async (id: string, type: BookmarkSectionType) => {
        let bookmarkItem: BookmarkItem | undefined = bookmarkItems?.find((item) => item.name === type);
        const action = bookmarkItem && bookmarkItem.bookmarks[id] ? 'remove' : 'add';

        if (!bookmarkItem) {
            bookmarkItem = {
                name: type,
                bookmarks: {
                    [id]: true,
                },
            };
            setBookmarkItems([...bookmarkItems, bookmarkItem]);
        } else {
            if (bookmarkItem.bookmarks[id]) {
                delete bookmarkItem.bookmarks[id];
            } else {
                bookmarkItem.bookmarks[id] = true;
            }
            setBookmarkItems([...bookmarkItems]);
        }

        await toggleBookmark(id, type, action);
        queryClient.resetQueries({ queryKey: ['watchlistCollection'] });
    };

    return {
        isBookmarked,
        toggleBookmarkItem,
        bookmarkItems,
        bookmarksInitialized: bookmarkItems != null,
        topCategoriesRef: topCategoriesRef.current,
        childCategoriesRef: childCategoriesRef.current,
        getBookmarks: () => bookmarksFetcher(null),
    };
};

export const BookmarksContext = createContext<ReturnType<typeof useBookmarksService>>(null);

export const BookmarksProvider = ({ children }) => {
    const context = useBookmarksService();

    return <BookmarksContext.Provider value={context}>{children}</BookmarksContext.Provider>;
};

export const useBookmarks = () => useContext(BookmarksContext);
